import { HttpParams } from '@angular/common/http'
import { Component, Input, OnDestroy, OnInit, WritableSignal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { DataMap } from '@grapecity/wijmo.grid'

import {
    AutoSizeDirective,
    BaseComponent,
    FeatureType,
    Grid,
    ListItem,
    ModalSize,
    QualityControlConcreteResult,
    VpJob,
} from '../../../shared'
import { ActionToolbarButtonsComponent } from '../../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { ConcreteResultModalComponent } from './concrete-result-modal.component'

@Component({
    selector: 'app-quality-control-concrete-result',
    templateUrl: './concrete-result.component.html',
    standalone: true,
    imports: [
        FormsModule,
        WjGridModule,
        WjGridFilterModule,
        WjInputModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        AutoSizeDirective,
    ],
})
export class ConcreteResultComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() selectedJob!: WritableSignal<string>
    @Input() allJobs: VpJob[] = []

    grid: Grid<QualityControlConcreteResult>
    concreteVendors: any[] = []
    supplierMap!: DataMap

    constructor() {
        super()
        this.grid = new Grid<QualityControlConcreteResult>(this.services)
            .setupModalEditing(
                this.services.data.qualityControlConcreteResults,
                'Concrete Test Result',
                ConcreteResultModalComponent,
                () => QualityControlConcreteResult.createNew(),
                this,
                ModalSize.large,
            )
            .filterBy(item => [
                item.reportNumber,
                item.labName,
                item.pourLocation,
                item.mixNo,
                item.inspector,
                item.reportSource,
            ])
            .withColumnFilters()
            .isReadOnly(true)
            .wrapColumnHeaders()

        this.grid.itemAdded.on(this.handleItemAdded.bind(this))
        this.grid.modified.on(this.refreshData.bind(this))
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsPlansAndSpecsEdit])
    }

    ngOnInit() {
        this.loadVendors()
    }

    ngOnDestroy(): void {
        this.grid.itemAdded.off(this.handleItemAdded.bind(this))
        this.grid.modified.off(this.refreshData.bind(this))
        this.grid.destroy()
    }

    loadVendors() {
        this.services.data.get<any[]>('api/Vendor/GetForConcrete').subscribe({
            next: (result: any[]) => {
                this.concreteVendors = result
                this.buildSupplierMap()
            },
            error: () => {
                this.services.notification.error('Failed to load vendors')
            },
        })
    }

    buildSupplierMap() {
        const vendorOptions: ListItem<number | null>[] = []
        vendorOptions.push(new ListItem<number | null>(null, ''))
        this.concreteVendors.forEach(x => {
            vendorOptions.push(new ListItem<number | null>(x.id, x.name))
        })
        this.supplierMap = new DataMap(vendorOptions, 'value', 'display')
    }

    loadConcreteResultData(jobId: string) {
        if (!jobId || jobId === '') {
            this.grid.items = []
            return
        }

        const params = new HttpParams().set('jobId', jobId)
        this.services.data
            .get<QualityControlConcreteResult[]>('api/QualityControlConcreteResult/GetByJob', params)
            .subscribe({
                next: (result: QualityControlConcreteResult[]) => {
                    this.grid.items = result || []
                },
                error: () => {
                    this.services.notification.error('Failed to load concrete test results')
                    this.grid.items = []
                },
            })
    }

    handleItemAdded(item: QualityControlConcreteResult) {
        item.job = this.selectedJob()
    }

    refreshData() {
        const jobId = this.selectedJob()
        if (jobId && jobId !== '') {
            this.loadConcreteResultData(jobId)
        }
    }

    onGridInitialized() {
        // Grid initialized
    }
}
